/**
 * NiyoValidate — field validation for the Niyo storefront forms (contact,
 * Silver Circle, newsletter). Mirrors the API rules (niyo-pricing
 * src/validators/fields.ts) so shoppers see the same messages before sending.
 *
 *   const v = NiyoValidate.attach(form, {
 *     'contact[email]': { label: 'Email address', rule: 'email', required: true },
 *   });
 *   if (!v.check()) return;               // shows inline errors, focuses the first
 *   v.showServer(json.details, { email: 'contact[email]' });   // API 400 details
 *
 * Errors appear under each field (aria-invalid + aria-describedby) and clear
 * as the shopper fixes them. Loaded by snippets/niyo-form-feedback.liquid.
 */
(function () {
  'use strict';
  if (window.NiyoValidate) return;

  var NAME_RE = /^[\p{L}][\p{L}\p{M} .'’-]*$/u;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var ORDER_RE = /^#?[A-Za-z0-9-]{3,20}$/;

  /** Indian mobile (10 digits from 6–9, optional +91/91/0) or international +<8–15 digits>. */
  function phoneOk(v) {
    var c = v.replace(/[\s().-]/g, '');
    return /^(?:\+?91|0)?[6-9]\d{9}$/.test(c) || /^\+[1-9]\d{7,14}$/.test(c);
  }

  function valueOf(el) {
    if (!el) return '';
    if (el.type === 'checkbox') return el.checked ? 'on' : '';
    return String(el.value || '').trim();
  }

  /** Returns an error message, or '' when the value is fine. */
  function checkValue(value, r, el) {
    var label = r.label || 'This field';
    if (!value) {
      if (!r.required) return '';
      if (r.rule === 'checkbox') return r.message || 'Please tick this box to continue';
      if (r.rule === 'select') return 'Please choose ' + label.toLowerCase();
      return label + ' is required';
    }
    switch (r.rule) {
      case 'name':
        if (value.length < 2) return label + ' must be at least 2 characters';
        if (value.length > 80) return label + ' is too long';
        if (!NAME_RE.test(value)) return label + ' can only contain letters, spaces, dots, apostrophes and hyphens';
        return '';
      case 'email':
        if (value.length > 254 || !EMAIL_RE.test(value)) return 'Please enter a valid email address, e.g. name@example.com';
        return '';
      case 'phone':
        return phoneOk(value) ? '' : 'Please enter a valid mobile number, e.g. +91 98765 43210';
      case 'order':
        return ORDER_RE.test(value) ? '' : 'Please enter the order number from your confirmation email, e.g. #1004';
      case 'huids': {
        var list = value.toUpperCase().split(/[\s,;]+/).filter(Boolean);
        var bad = list.filter(function (h) { return !/^[A-Z0-9]{6}$/.test(h); });
        if (bad.length) return bad.join(', ') + ' is not a valid HUID — each HUID is 6 letters and numbers, e.g. 83LS3B';
        if (list.length > 20) return 'Please submit at most 20 pieces per request';
        return '';
      }
      case 'number': {
        var n = Number(value);
        if (!isFinite(n)) return label + ' must be a number';
        if (r.min !== undefined && n < r.min) return label + ' must be at least ' + r.min + (r.unit || '');
        if (r.max !== undefined && n > r.max) return label + ' must be at most ' + r.max + (r.unit || '');
        return '';
      }
      case 'text': {
        if (r.min && value.length < r.min) return label + ' must be at least ' + r.min + ' characters';
        if (r.max && value.length > r.max) return label + ' is too long (max ' + r.max + ' characters)';
        if ((value.match(/https?:\/\//gi) || []).length > 3) return label + ' has too many links';
        return '';
      }
      default:
        return '';
    }
  }

  function attach(form, rules, options) {
    options = options || {};
    form.noValidate = true; // our messages replace the browser's bubbles
    var touched = {};

    function field(name) {
      var el = form.elements.namedItem(name);
      // RadioNodeList → first input (errors sit after the group)
      if (el && typeof el.length === 'number' && !el.tagName) return el[0];
      return el;
    }

    function anchorFor(el) {
      // Put the message after the field's wrapper when the field sits in a label/choice row.
      return (el.closest && el.closest('[data-field]')) || (el.type === 'checkbox' ? el.closest('label') || el : el);
    }

    function messageEl(name, el) {
      var id = (el.id || name.replace(/\W+/g, '-')) + '-error';
      var msg = document.getElementById(id);
      if (!msg) {
        msg = document.createElement('p');
        msg.id = id;
        msg.className = 'niyo-field-error';
        msg.hidden = true;
        var anchor = anchorFor(el);
        anchor.insertAdjacentElement('afterend', msg);
      }
      return msg;
    }

    function setError(name, text) {
      var el = field(name);
      if (!el) return;
      var msg = messageEl(name, el);
      var described = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (text) {
        msg.textContent = text;
        msg.hidden = false;
        el.setAttribute('aria-invalid', 'true');
        if (described.indexOf(msg.id) === -1) described.push(msg.id);
      } else {
        msg.textContent = '';
        msg.hidden = true;
        el.removeAttribute('aria-invalid');
        described = described.filter(function (d) { return d !== msg.id; });
      }
      if (described.length) el.setAttribute('aria-describedby', described.join(' '));
      else el.removeAttribute('aria-describedby');
    }

    function validateField(name) {
      var r = typeof rules[name] === 'function' ? rules[name]() : rules[name];
      if (!r) return true;
      var el = field(name);
      var error = checkValue(valueOf(el), r, el);
      setError(name, error);
      return !error;
    }

    function check() {
      var firstBad = null;
      Object.keys(rules).forEach(function (name) {
        touched[name] = true;
        if (!validateField(name) && !firstBad) firstBad = field(name);
      });
      if (firstBad) {
        firstBad.focus({ preventScroll: true });
        firstBad.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        if (options.onInvalid) options.onInvalid();
      }
      return !firstBad;
    }

    /** API 400 → messages under the matching fields. `map` is apiKey → form field name. */
    function showServer(details, map) {
      var shown = false;
      Object.keys(details || {}).forEach(function (key) {
        var name = (map && map[key]) || key;
        if (field(name)) {
          setError(name, details[key][0]);
          if (!shown) field(name).focus();
          shown = true;
        }
      });
      return shown;
    }

    function clear() {
      Object.keys(rules).forEach(function (name) {
        setError(name, '');
        touched[name] = false;
      });
    }

    // Re-check a field as the shopper types, once it has been flagged/visited.
    Object.keys(rules).forEach(function (name) {
      var el = field(name);
      if (!el) return;
      el.addEventListener('blur', function () {
        if (valueOf(el)) {
          touched[name] = true;
          validateField(name);
        }
      });
      el.addEventListener(el.tagName === 'SELECT' || el.type === 'checkbox' ? 'change' : 'input', function () {
        if (touched[name]) validateField(name);
      });
    });

    return { check: check, showServer: showServer, clear: clear, validateField: validateField };
  }

  window.NiyoValidate = { attach: attach, phoneOk: phoneOk };
})();
